import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Activity, Brain, Watch, Salad, LineChart, ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { organizationSchema, wellnessWebPageSchema, OG_IMAGES, SITE_URL} from "@/lib/seo";
import {
  wearableHeroLeft,
  wearableHeroRight,
  sectionTitleFade,
  fadeUpInViewStaggered,
} from "./_partials/animationVariants";

const AGENT_URL = process.env.REACT_APP_JOTFORM_INTAKE_URL;

const SERVICES = [
  {
    icon: Brain,
    title: "AI Wellness Coaching",
    body:
      "One-on-one coaching, augmented by your Digital Twin. Your data sets the plan — nutrition, training, sleep, recovery, supplement stack.",
  },
  {
    icon: Salad,
    title: "Personalized Supplement Protocols",
    body:
      "No generic stacks. Your supplement plan is built from your bloodwork, DNA, microbiome, and lifestyle — and re-tuned as your data evolves.",
  },
  {
    icon: Activity,
    title: "Lifestyle Optimization",
    body:
      "Sleep architecture, training load, recovery windows, stress dosing — the daily protocols that move the needle on energy, focus, and resilience.",
  },
  {
    icon: Sparkles,
    title: "Performance Wellness Coaching",
    body:
      "Built for executives, founders, athletes, and high-output professionals — wellness designed to raise the ceiling, not just survive the week.",
  },
  {
    icon: Watch,
    title: "Wearable Data Integration",
    body:
      "Apple Health, Oura, Whoop, Fitbit, Garmin — all your signals streaming into one wellness record, fused into actionable, daily insight.",
  },
  {
    icon: LineChart,
    title: "Digital Twin Wellness Analysis",
    body:
      "A continuously updated, data-driven model of you — your inputs, your outputs, your trajectory. Wellness, finally measured.",
  },
];

export default function WellnessOptimization() {
  useDocumentMeta({
    title:
      "Health Optimization Programs Nationwide | Body, Performance & Longevity | Integrated Wellness",
    description:
      "Integrated Optimization Programs delivered nationwide — personalized lifestyle, supplement, nutrition and training protocols built from your data, with licensed physician oversight built in.",
    image: OG_IMAGES.wellnessOptimization,
    url: `${SITE_URL}/wellness-optimization`,
    schema: [
      organizationSchema(),
      wellnessWebPageSchema({
        name: "AI-Driven Wellness Optimization | Integrated Clinical Intelligence",
      }),
    ],
  });

  return (
    <div data-testid="wellness-optimization-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-28 md:pt-32 pb-24">
        <section className="max-w-6xl mx-auto px-4 md:px-8">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.32em] text-accent hover:text-primary transition-colors"
          >
            ← Back to Integrated Clinical Intelligence
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <motion.div {...wearableHeroLeft} className="lg:col-span-7">
              <div className="iw-overline mb-5">Integrated Optimization Programs
              </div>
              <h1 className="font-display font-bold tracking-[-0.04em] leading-[1.0] text-[clamp(2.5rem,5.5vw,4.75rem)]">
                One program. <em className="not-italic text-primary">Every system optimized</em>.
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-foreground/80 leading-relaxed max-w-xl">
                Personalized lifestyle, supplement, nutrition, and training protocols built from
                your data — with{" "}
                <strong className="text-foreground">licensed physician oversight built in</strong>.
                Your Digital Twin builds the blueprint; your care team keeps it clinical.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a  href={AGENT_URL} data-testid="wellness-analysis-cta"
                  className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]">
                  Start My Health Assessment <ArrowRight size={16} aria-hidden />
                </a>
                <Link
                  to="/medical-care"
                  data-testid="wellness-to-medical"
                  className="iw-btn-ghost inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
                >
                  Physician oversight, built in <span className="text-primary">→</span>
                </Link>
              </div>
            </motion.div>

            <motion.aside {...wearableHeroRight} className="lg:col-span-5">
              <div className="rounded-2xl border-2 border-accent/30 bg-card p-7 md:p-8 shadow-md">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent mb-3">
                  One Program · Clinical Oversight Included
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
                  Optimization and medical care — <em className="not-italic text-primary">no longer separate</em>.
                </h2>
                <p className="mt-4 text-base text-foreground/75 leading-relaxed">
                  Every Integrated Optimization Program combines data-driven coaching with licensed
                  physician oversight — clinical care when your labs call for it, precision coaching
                  every day in between. One program, built on your unique biology.
                </p>
              </div>
            </motion.aside>
          </div>
        </section>

        {/* Services */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
          <motion.div {...sectionTitleFade}>
            <div className="iw-overline mb-4">Wellness Services
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-3xl">
              Wellness, <em className="not-italic text-primary">finally measurable</em>.
            </h2>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {SERVICES.map(({ icon: Icon, title, body }, i) => (
              <motion.div
                key={title}
                {...fadeUpInViewStaggered(i * 0.05)}
                data-testid={`wellness-service-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="premium-card p-6 md:p-7 bg-card border-2 border-border rounded-2xl hover:border-primary/45 transition-colors"
              >
                <Icon size={26} className="text-primary mb-4" aria-hidden />
                <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight">{title}</h3>
                <p className="mt-3 text-base text-foreground/75 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 mt-24 md:mt-32 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
            Your wellness blueprint <em className="not-italic text-primary">in minutes</em>.
          </h2>
          <p className="mt-5 text-lg md:text-xl text-foreground/75 leading-relaxed">
            Free Digital Twin wellness analysis — your goals, your data, your daily plan.
          </p>
          <a  href={AGENT_URL} data-testid="wellness-closing-cta"
            className="mt-8 iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.75rem]">
            Start My Health Assessment <ArrowRight size={18} aria-hidden />
          </a>
        </section>
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
